import moment from 'moment';
import { findIana } from 'windows-iana';
import { DateTimeInfo } from './../types/_general/date-time.interface';

function formatMomentDateAndTime(date: string | null, format = ''): DateTimeInfo | null {
    if (date === null || date === undefined) {
        return null;
    }

    return {
        date: TimezoneHelper.formatMomentDate(date, false, format),
        time: TimezoneHelper.formatMomentTime(date),
    };
}

function formatMomentDate(date: string | null, includeHours = false, format = ''): string {
    if (date === null || date === undefined) {
        return '';
    }

    return moment(date).format(format ? format : 'MMM DD, YYYY' + (includeHours ? ' @ h:mma' : ''));
}

function formatMomentTime(date: string | null): string {
    if (date === null || date === undefined) {
        return '';
    }

    return moment(date).format('h:mma');
}

function startOfDay(date: Date): Date {
    // @ts-expect-error - window.timeZoneOffset is a global variable
    let offset = window.timeZoneOffset;
    if (offset >= 0) {
        offset = '+' + offset;
    }
    const d = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} 00:00:00`;
    return moment(`${d}${offset}:00`).utc().toDate();
}

function endOfDay(date: Date): Date {
    // @ts-expect-error - window.timeZoneOffset is a global variable
    let offset = window.timeZoneOffset;
    if (offset >= 0) {
        offset = '+' + offset;
    }
    const d = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} 23:59:59`;
    return moment(`${d}${offset}:00`).utc().toDate();
}

function localTimeToUserTimezone(date: Date, format = 'MMM DD, YYYY'): string {
    const day = date.getDate().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNameShort = date.toLocaleDateString('en-US', { month: 'short' });

    switch (format) {
        case 'DD/MM/YYYY':
            return `${day.padStart(2, '0')}/${month}/${year}`;
        case 'MM/DD/YYYY':
            return `${month}/${day.padStart(2, '0')}/${year}`;
        case 'YYYY/MM/DD':
            return `${year}/${month}/${day.padStart(2, '0')}`;
        case 'MMM DD, YYYY':
            return `${monthNameShort} ${day}, ${year}`;
    }

    return `${month}/${day.padStart(2, '0')}/${year}`;
}

function datePickerEndOfCurrentDateUTC(): Date {
    const d1 = moment().utc().format('DD/MM/YYYY').split('/');
    const d2 = Date.UTC(Number(d1[2]), Number(d1[1]) - 1, Number(d1[0]));
    const d3 = new Date(d2).setHours(23, 59, 59, 999);
    return new Date(d3);
}

function datePickerEndOfCurrentDate(): Date {
    const d1 = moment().format('DD/MM/YYYY').split('/');
    const d2 = Date.UTC(Number(d1[2]), Number(d1[1]) - 1, Number(d1[0]));
    const d3 = new Date(d2).setHours(23, 59, 59, 999);
    return new Date(d3);
}

/**
 * Converts a date-only picker value to UTC ISO string using the selected date and the current time (now)
 * in the logged-in user's timezone. Use when the user only picks a date and the time should be "now".
 */
function dateInUserTimezoneWithCurrentTimeToUTCISO(date: Date | null): string | null {
    if (date === null) {
        return null;
    }
    const zone = (moment as any).tz?.defaultZone?.name ?? 'UTC';
    const nowInZone = moment().tz(zone);
    const m = moment.tz(
        {
            year: date.getFullYear(),
            month: date.getMonth(),
            date: date.getDate(),
            hour: nowInZone.hour(),
            minute: nowInZone.minute(),
            second: nowInZone.second(),
            millisecond: nowInZone.millisecond(),
        },
        zone
    );
    return m.toISOString();
}

function computeTimezoneOffset(timezone: string): number {
    const iana = TimezoneHelper.windowsToIana(timezone);
    const offset = moment.tz(iana).utcOffset();
    const offsetHours = offset / 60;
    return offsetHours;
}

function windowsToIana(windowsTimezone: string): string {
    const result = findIana(windowsTimezone);
    if (result.length > 0 && result[0]) {
        return result[0];
    }

    //no iana result
    if (windowsTimezone === 'Mid-Atlantic Standard Time') {
        return 'Etc/GMT+2';
    }

    //no iana result
    if (windowsTimezone === 'Kamchatka Standard Time') {
        return 'Asia/Kamchatka';
    }

    return '';
}

export const TimezoneHelper = {
    formatMomentDateAndTime,
    formatMomentDate,
    formatMomentTime,
    startOfDay,
    endOfDay,
    localTimeToUserTimezone,
    datePickerEndOfCurrentDate,
    datePickerEndOfCurrentDateUTC,
    dateInUserTimezoneWithCurrentTimeToUTCISO,
    computeTimezoneOffset,
    windowsToIana,
};
